"use client"

import * as React from "react"
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu"
import { Check, ChevronRight, Circle } from "lucide-react"

import { cn } from "@/lib/utils"
import { usePopupLayer } from "@/lib/layers/LayerOwner"
import { OPTION_TONES, overlay, type OptionTone } from "./overlay-recipes"

/**
 * ONE look for every menu (docs/THEME.md 2.10; std-overlays G01): the popup recipe shared with Select, Popover and the
 * listboxes (components/ui/overlay-recipes.ts): bg-popup, border-edge, rounded-overlay, shadow-float, p-1, 30px rows
 * with the hover fill. Since G01 this is the default for every caller; `look` is kept so existing
 * `look="standard"` opt-ins still type-check, and both values render the same.
 * The content portals into its OWNER's layer slot when inside a migrated modal or the page layer root; with no owner
 * it keeps today's body portal and z.
 */
export type DropdownMenuLook = "legacy" | "standard"

function DropdownMenu({ look, ...props }: React.ComponentProps<typeof DropdownMenuPrimitive.Root> & { look?: DropdownMenuLook }) {
  void look
  return <DropdownMenuPrimitive.Root {...props} />
}

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger

const DropdownMenuGroup = DropdownMenuPrimitive.Group

const DropdownMenuPortal = DropdownMenuPrimitive.Portal

const DropdownMenuSub = DropdownMenuPrimitive.Sub

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup

const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
    inset?: boolean
  }
>(({ className, inset, children, ...props }, ref) => {
  return (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    className={cn(
      overlay.option,
      "data-[state=open]:bg-hover",
      inset && overlay.optionInset,
      className
    )}
    {...props}
  >
    {children}
    <ChevronRight className="ml-auto icon" aria-hidden="true" />
  </DropdownMenuPrimitive.SubTrigger>
  )
})
DropdownMenuSubTrigger.displayName =
  DropdownMenuPrimitive.SubTrigger.displayName

// Menu layer (SubContent and Content): z 100102, ABOVE every dialog layer (Dialog overlay 10000 / content 10001,
// DialogContentNested 100100 / 100101). Portalled to body, so z-50 put a menu opened inside a Dialog BEHIND the dialog's
// overlay (UI review 2026-10-23, call-setup B07). Local value until the layer z-scale is decided (STANDARD open item).
/** The legacy (unowned) menu z class, kept verbatim for unmigrated callers. */
const LEGACY_MENU_Z = "z-[100102]"

/** The menu surface (Content and SubContent): the shared popup recipe, scrolling inside the available height. */
const MENU_SURFACE = cn(
  "min-w-[8rem] max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto",
  overlay.popupSurface,
  overlay.popupMaxWidth,
  "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
)

const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({ className, ...props }, ref) => {
  const layer = usePopupLayer(LEGACY_MENU_Z)
  return (
  <DropdownMenuPrimitive.SubContent
    ref={ref}
    className={cn(MENU_SURFACE, layer.zClass, className)}
    {...props}
  />
  )
})
DropdownMenuSubContent.displayName =
  DropdownMenuPrimitive.SubContent.displayName

const DIALOG_SELECTOR = '[role="dialog"], [role="alertdialog"]'

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 4, onCloseAutoFocus, ...props }, ref) => {
  // Owned (inside a migrated modal or the page layer root): the owner's slot. No owner: body at the legacy menu z as before.
  const layer = usePopupLayer(LEGACY_MENU_Z)
  // WCAG 2.4.3: a dialog opened from a menu item appears while the menu is open. The menu's focus trap bounces the dialog's
  // own initial focus, and once the menu unmounts Radix puts focus on the trigger on a timer: behind the dialog. When the menu
  // closes and a dialog that was not there when it opened exists, leave focus with that dialog instead of the trigger.
  const dialogsAtOpenRef = React.useRef<Set<Element>>(new Set())
  const contentNodeRef = React.useRef<Element | null>(null)
  // Radix mounts the content element only while open, so a NEW content element is the open moment
  // (DropdownMenu.Content has no onOpenAutoFocus prop: that exists on Dialog/Popover only). Radix re-calls this ref on
  // re-renders with the same element (null, then the node), including after the dialog mounts: snapshot once per element.
  const setContentRef = React.useCallback(
    (node: React.ElementRef<typeof DropdownMenuPrimitive.Content> | null) => {
      if (node && node !== contentNodeRef.current) {
        contentNodeRef.current = node
        dialogsAtOpenRef.current = new Set(document.querySelectorAll(DIALOG_SELECTOR))
      }
      if (typeof ref === "function") ref(node)
      else if (ref) ref.current = node
    },
    [ref],
  )
  return (
  <DropdownMenuPrimitive.Portal container={layer.container}>
    <DropdownMenuPrimitive.Content
      ref={setContentRef}
      data-popup-owned={layer.owned ? "" : undefined}
      sideOffset={sideOffset}
      onCloseAutoFocus={(event) => {
        onCloseAutoFocus?.(event)
        if (event.defaultPrevented) return
        // Focus in an embedded page is an outside choice; leave it there after frame dismissal.
        if (document.activeElement?.tagName === "IFRAME") {
          event.preventDefault()
          return
        }
        const opened = [...document.querySelectorAll<HTMLElement>(DIALOG_SELECTOR)].filter((d) => !d.hasAttribute("data-persistent-notice") && !dialogsAtOpenRef.current.has(d))
        const dialog = opened[opened.length - 1]
        if (!dialog) return
        event.preventDefault()
        if (!dialog.contains(document.activeElement)) dialog.focus()
      }}
      className={cn(MENU_SURFACE, layer.zClass, className)}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
  )
})
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName

/** An icon: a component (lucide) or an element. */
export type OptionIcon = React.ElementType<{ className?: string; "aria-hidden"?: boolean | "true" | "false" }> | React.ReactElement

/**
 * The rich slots of one option row (docs/THEME.md 2.10): a leading icon (muted, toned, or on a toned tile), a colour
 * swatch, a second line, a trailing badge, a toned label and the current-item marker. Every colour is a token
 * (OptionTone); feature code passes slots, never classes.
 */
export interface OptionSlots {
  icon?: OptionIcon | null
  /** The icon's colour. */
  iconTone?: OptionTone
  /** Put the icon on a tinted square (in the icon's tone, or the raised fill without one). */
  iconTile?: boolean
  /** A colour swatch before the label. */
  swatch?: OptionTone
  /** A short second line under the label. */
  description?: React.ReactNode
  /** A trailing count or tag. */
  badge?: React.ReactNode
  /** The label's colour (the current page, an active mode). */
  labelTone?: OptionTone
  /** The trailing dot of the current item, in the label tone (or the icon tone). */
  marker?: boolean
}

const SLOT_KEYS = ["icon", "iconTone", "iconTile", "swatch", "description", "badge", "labelTone", "marker"] as const

/** Split the rich slots from the other props; `has` is true when any slot is given. */
function takeSlots<P extends OptionSlots>(props: P): { slots: OptionSlots; rest: Omit<P, keyof OptionSlots>; has: boolean } {
  const slots: OptionSlots = {}
  const rest = { ...props } as Record<string, unknown>
  let has = false
  for (const key of SLOT_KEYS) {
    if (key in rest) {
      const value = rest[key]
      delete rest[key]
      if (value !== undefined && value !== null && value !== false) {
        ;(slots as Record<string, unknown>)[key] = value
        has = true
      }
    }
  }
  return { slots, rest: rest as Omit<P, keyof OptionSlots>, has }
}

function renderIcon(icon: OptionIcon): React.ReactNode {
  if (React.isValidElement(icon)) return icon
  const Icon = icon as React.ElementType
  return <Icon className="icon" aria-hidden="true" />
}

/**
 * The content of one rich option row. DropdownMenuItem / CheckboxItem / RadioItem render it from their slot props;
 * a row that is a link (`asChild` around a Link) renders it inside the link.
 */
function MenuOptionContent({
  icon,
  iconTone,
  iconTile,
  swatch,
  description,
  badge,
  labelTone,
  marker,
  children,
}: OptionSlots & { children?: React.ReactNode }) {
  const markerTone = labelTone ?? iconTone ?? "brand"
  return (
    <>
      {swatch ? <span data-option-swatch={swatch} aria-hidden="true" className={cn(overlay.optionSwatch, OPTION_TONES[swatch].dot)} /> : null}
      {icon ? (
        iconTile ? (
          <span
            data-option-icon={iconTone ?? ""}
            data-option-tile=""
            aria-hidden="true"
            className={cn(overlay.optionIconTile, iconTone ? OPTION_TONES[iconTone].tile : overlay.optionIconTileNeutral)}
          >
            {renderIcon(icon)}
          </span>
        ) : (
          <span
            data-option-icon={iconTone ?? ""}
            aria-hidden="true"
            className={cn("inline-flex shrink-0", iconTone ? OPTION_TONES[iconTone].text : "text-ink-muted")}
          >
            {renderIcon(icon)}
          </span>
        )
      ) : null}
      <span className="min-w-0 flex-1">
        <span data-option-label={labelTone ?? ""} className={cn("block truncate", labelTone && OPTION_TONES[labelTone].text)}>
          {children}
        </span>
        {description ? <span data-option-description="" className={overlay.optionDescription}>{description}</span> : null}
      </span>
      {badge !== undefined && badge !== null && badge !== false ? <span data-option-badge="" className={overlay.optionBadge}>{badge}</span> : null}
      {marker ? <span data-option-marker="" aria-hidden="true" className={cn(overlay.optionMarker, OPTION_TONES[markerTone].dot)} /> : null}
    </>
  )
}

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & OptionSlots & {
    inset?: boolean
    /**
     * "danger" for an item that removes or deletes something (danger text, danger-fill highlight); any other tone
     * colours the row's text (a highlighted action).
     */
    tone?: "default" | OptionTone
  }
>(({ className, inset, tone = "default", children, asChild, ...all }, ref) => {
  const { slots, rest, has } = takeSlots(all)
  return (
  <DropdownMenuPrimitive.Item
    ref={ref}
    asChild={asChild}
    data-tone={tone === "default" ? undefined : tone}
    className={cn(
      overlay.option,
      tone === "danger" ? "text-danger data-[highlighted]:bg-danger-fill" : tone !== "default" && OPTION_TONES[tone].text,
      has && slots.description ? "py-1.5" : undefined,
      inset && overlay.optionInset,
      className,
    )}
    {...rest}
  >
    {has && !asChild ? <MenuOptionContent {...slots}>{children}</MenuOptionContent> : children}
  </DropdownMenuPrimitive.Item>
  )
})
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName

const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem> & OptionSlots
>(({ className, children, checked, ...all }, ref) => {
  const { slots, rest, has } = takeSlots(all)
  return (
  <DropdownMenuPrimitive.CheckboxItem
    ref={ref}
    className={cn(overlay.option, overlay.optionInset, overlay.optionChecked, has && slots.description ? "py-1.5" : undefined, className)}
    checked={checked}
    {...rest}
  >
    <span className={overlay.optionIndicator}>
      <DropdownMenuPrimitive.ItemIndicator>
        <Check className="icon" aria-hidden="true" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {has ? <MenuOptionContent {...slots}>{children}</MenuOptionContent> : children}
  </DropdownMenuPrimitive.CheckboxItem>
  )
})
DropdownMenuCheckboxItem.displayName =
  DropdownMenuPrimitive.CheckboxItem.displayName

const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem> & OptionSlots
>(({ className, children, ...all }, ref) => {
  const { slots, rest, has } = takeSlots(all)
  return (
  <DropdownMenuPrimitive.RadioItem
    ref={ref}
    className={cn(overlay.option, overlay.optionInset, overlay.optionChecked, has && slots.description ? "py-1.5" : undefined, className)}
    {...rest}
  >
    <span className={overlay.optionIndicator}>
      <DropdownMenuPrimitive.ItemIndicator>
        <Circle className="h-2 w-2 fill-current" aria-hidden="true" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {has ? <MenuOptionContent {...slots}>{children}</MenuOptionContent> : children}
  </DropdownMenuPrimitive.RadioItem>
  )
})
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName

const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean
    /** A category heading in its colour: a leading dot and toned text. */
    tone?: OptionTone
  }
>(({ className, inset, tone, children, ...props }, ref) => {
  return (
  <DropdownMenuPrimitive.Label
    ref={ref}
    data-tone={tone}
    className={cn(overlay.groupLabel, tone && cn("flex items-center gap-2", OPTION_TONES[tone].text), inset && overlay.optionInset, className)}
    {...props}
  >
    {tone ? <span data-label-dot="" aria-hidden="true" className={cn(overlay.groupLabelDot, OPTION_TONES[tone].dot)} /> : null}
    {children}
  </DropdownMenuPrimitive.Label>
  )
})
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn(overlay.separator, className)}
    {...props}
  />
))
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName

const DropdownMenuShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn(overlay.shortcut, className)}
      {...props}
    />
  )
}
DropdownMenuShortcut.displayName = "DropdownMenuShortcut"

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
  MenuOptionContent,
}
